import React, { useState, useEffect } from "react";
import { PasswordInput, TextInput } from "../form/inputs";
import PrimaryBtn from "../button/PrimaryBtn";

const Changepass = ({ open, onClose, onSubmit }) => {
  const initialState = {
    old_pass: "",
    new_pass: "",
    confirm_pass: "",
  };

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});

  // ✅ Reset everything when modal closes
  useEffect(() => {
    if (!open) {
      setForm(initialState);
      setErrors({});
    }
  }, [open]);

  if (!open) return null;

  // 🔐 Password Validation
  const validatePassword = (password) => {
    if (!password) return "New password is required";

    if (/\s/.test(password)) return "Whitespace is not allowed";

    if (!/[A-Z]/.test(password))
      return "Must contain at least one uppercase letter";

    if (!/[a-z]/.test(password))
      return "Must contain at least one lowercase letter";

    if (password.length < 8 || password.length > 16)
      return "Password must be 8 to 16 characters";

    if (!/[0-9]/.test(password)) return "Must contain at least one number";

    if (!/[!@#$%^&*]/.test(password))
      return "Must contain at least one special character (!@#$%^&*)";

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 🚫 Prevent whitespace typing completely
    if (/\s/.test(value)) return;

    setForm((prev) => {
      const updatedForm = { ...prev, [name]: value };

      let newErrors = { ...errors };

      if (name === "new_pass") {
        const passError = validatePassword(value);
        if (passError) newErrors.new_pass = passError;
        else delete newErrors.new_pass;

        // also re-check confirm password live
        if (updatedForm.confirm_pass && updatedForm.confirm_pass !== value) {
          newErrors.confirm_pass = "Passwords do not match";
        } else {
          delete newErrors.confirm_pass;
        }
      }

      if (name === "confirm_pass") {
        if (value !== updatedForm.new_pass) {
          newErrors.confirm_pass = "Passwords do not match";
        } else {
          delete newErrors.confirm_pass;
        }
      }

      setErrors(newErrors);
      return updatedForm;
    });
  };

  const handleSubmit = () => {
    let newErrors = {};

    const passError = validatePassword(form.new_pass);
    if (passError) newErrors.new_pass = passError;

    if (form.new_pass !== form.confirm_pass)
      newErrors.confirm_pass = "Passwords do not match";

    if (!form.old_pass) newErrors.old_pass = "Old password is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(form);

    // ✅ Clear after successful submit
    setForm(initialState);
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl bg-white rounded-2xl shadow-2xl z-50 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-14 border-b">
          <h1 className="text-base sm:text-lg font-semibold">
            Change Password
          </h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black text-lg"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          <div>
            <TextInput
              label="Old Password"
              name="old_pass"
              value={form.old_pass}
              onChange={handleChange}
            />
            {errors.old_pass && (
              <p className="text-red-500 text-sm mt-1">{errors.old_pass}</p>
            )}
          </div>

          <div>
            <PasswordInput
              label="New Password"
              name="new_pass"
              value={form.new_pass}
              onChange={handleChange}
            />
            {errors.new_pass && (
              <p className="text-red-500 text-sm mt-1">{errors.new_pass}</p>
            )}
          </div>

          <div>
            <PasswordInput
              label="Confirm New Password"
              name="confirm_pass"
              value={form.confirm_pass}
              onChange={handleChange}
            />
            {errors.confirm_pass && (
              <p className="text-red-500 text-sm mt-1">{errors.confirm_pass}</p>
            )}
          </div>

          <div className="pt-2">
            <PrimaryBtn btnName="Change Password" clickEvent={handleSubmit} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Changepass;
